import React, { useState } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useRef } from 'react';
import AdminLayout from '../../layouts/admin.layout';
import { useUsers } from '../hooks';
import { UsersTable, UserSidebar } from '../components';
import { User } from '../types';
import './users.screen.css';

const UsersScreen: React.FC = () => {
  const { users, loading, error, addUser, editUser, removeUser } = useUsers();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const toast = useRef<Toast>(null);

  // Mostrar mensaje de éxito
  const showSuccess = (message: string) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Éxito',
      detail: message,
      life: 3000
    });
  };

  // Mostrar mensaje de error
  const showError = (message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 3000
    });
  };

  // Manejar creación de usuario
  const handleCreate = () => {
    setSelectedUser(undefined);
    setIsEditMode(false);
    setSidebarVisible(true);
  };

  // Manejar edición de usuario
  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsEditMode(true);
    setSidebarVisible(true);
  };

  // Manejar eliminación de usuario
  const handleDelete = async (id: number) => {
    try {
      const success = await removeUser(id);
      if (success) {
        showSuccess('Usuario eliminado correctamente');
        return true;
      } else {
        showError('No se pudo eliminar el usuario');
        return false;
      }
    } catch (err) {
      showError('Error al eliminar usuario');
      return false;
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (userData: { id?: number; email: string; password?: string; role: string }) => {
    try {
      if (isEditMode && userData.id) {
        // Editar usuario existente
        const success = await editUser(userData.id, userData);
        if (success) {
          showSuccess('Usuario actualizado correctamente');
          setSidebarVisible(false);
          return true;
        } else {
          showError('No se pudo actualizar el usuario');
          return false;
        }
      } else {
        // Crear nuevo usuario
        const success = await addUser({
          email: userData.email,
          password: userData.password || '',
          role: userData.role
        });
        if (success) {
          showSuccess('Usuario creado correctamente');
          setSidebarVisible(false);
          return true;
        } else {
          showError('No se pudo crear el usuario');
          return false;
        }
      }
    } catch (err) {
      showError('Error al procesar la solicitud');
      return false;
    }
  };

  return (
    <AdminLayout>
      <div className="users-screen">
        <Toast ref={toast} />
        <ConfirmDialog />

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <UsersTable 
          users={users} 
          loading={loading} 
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreate={handleCreate}
        />

        <UserSidebar
          visible={sidebarVisible}
          onHide={() => setSidebarVisible(false)}
          selectedUser={selectedUser}
          onSubmit={handleSubmit}
          isEditMode={isEditMode}
        />
      </div>
    </AdminLayout>
  );
};

export default UsersScreen; 